<template>
  <div class="dashboard dashboard-lab">
    <b-container fluid class="dashboard-lab-container">
      <section class="welcome-panel">
        <div class="welcome-copy">
          <h2>{{ labels.welcomeTitle }}</h2>
        </div>
      </section>

      <section class="outside-toolbar">
        <button
          v-if="canManageTags"
          class="btn btn-info btn-sm"
          @click="handleCreateClick"
        >
          {{ labels.createTag }}
        </button>
        <small v-else class="readonly-badge">{{ labels.readOnly }}</small>
        <small>{{ labels.tagCounter(tags.length, maxTags) }}</small>
      </section>

      <section v-if="showCreator" class="creator-panel">
        <div class="creator-card">
          <h5>{{ labels.creatorTitle }}</h5>
          <div class="creator-grid">
            <div>
              <label class="form-label">{{ labels.tagTitle }}</label>
              <input
                v-model="draft.title"
                class="form-control form-control-sm"
                :placeholder="labels.tagTitlePlaceholder"
              >
            </div>
            <div>
              <label class="form-label">{{ labels.infoType }}</label>
              <select v-model="draft.infoKey" class="form-select form-select-sm">
                <option v-for="opt in infoOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="creator-field-full">
              <label class="form-label">{{ labels.presentationType }}</label>
              <select v-model="draft.presentationType" class="form-select form-select-sm">
                <option v-for="opt in creatorPresentationOptions" :key="`creator_pres_${opt.value}`" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <div class="presentation-preview">
                <span class="presentation-thumb" :class="presentationThumbClass(draft.presentationType)"></span>
                <small>{{ presentationLabel(draft) }}</small>
              </div>
            </div>
          </div>
          <div class="creator-actions">
            <button class="btn btn-info btn-sm" :disabled="isLimitReached" @click="createTag">
              {{ labels.saveTag }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="showCreator = false">
              {{ labels.cancel }}
            </button>
          </div>
        </div>
      </section>

      <section
        ref="canvas"
        class="tags-canvas"
        :class="{ 'no-grid': !canManageTags }"
      >
        <div v-if="!tags.length" class="canvas-empty">
          <p>{{ labels.emptyState }}</p>
        </div>

        <article
          v-for="tag in tags"
          :key="tag.id"
          class="mini-tag"
          :class="{ active: activeTagId === tag.id, readonly: !canManageTags, editing: editingTagId === tag.id }"
          :style="tagStyle(tag)"
          @pointerdown="startDrag($event, tag.id)"
        >
          <header class="mini-tag-header">
            <strong>{{ tag.title }}</strong>
            <div class="mini-tag-actions">
              <button
                v-if="canManageTags && !isComportamientoNota(tag)"
                class="mini-edit"
                @pointerdown.stop
                @click="openTagEditor(tag.id)"
              >
                ⋮
              </button>
              <button
                v-if="canManageTags && !isComportamientoNota(tag)"
                class="mini-delete"
                @pointerdown.stop
                @click="deleteTag(tag.id)"
              >
                ×
              </button>
            </div>
          </header>

          <div class="mini-tag-body">
            <template v-if="isComportamientoPl(tag)">
              <div v-if="editingTagId === tag.id" class="pl-editor" @pointerdown.stop>
                <div class="inline-grid pl-grid">
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.fechaInicial }}</small>
                    <input
                      v-model="editDraft.fechaInicial"
                      type="date"
                      class="form-control form-control-sm inline-input"
                    >
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.fechaFinal }}</small>
                    <input
                      v-model="editDraft.fechaFinal"
                      type="date"
                      class="form-control form-control-sm inline-input"
                    >
                  </div>
                  <div v-if="plDatesReady" class="inline-field">
                    <small class="inline-label">{{ labels.arrendador }}</small>
                    <select
                      v-model="editDraft.arrendador"
                      class="form-select form-select-sm inline-input"
                    >
                      <option value="">{{ labels.selectOption }}</option>
                      <option
                        v-for="tercero in plTerceroOptions"
                        :key="`pl_tercero_${tercero.identificacion}`"
                        :value="tercero.identificacion"
                      >
                        {{ tercero.nombre }} - {{ tercero.identificacion }}
                      </option>
                    </select>
                  </div>
                  <div v-else class="inline-field pl-hint">
                    <small class="inline-label">{{ labels.arrendador }}</small>
                    <small class="text-muted">{{ isSpanish ? "Primero selecciona fechas inicial y final." : "Pick start and end dates first." }}</small>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.tipoContrato }}</small>
                    <select
                      v-model="editDraft.tipoContrato"
                      class="form-select form-select-sm inline-input"
                      :disabled="!plDatesReady"
                    >
                      <option value="">{{ labels.selectOption }}</option>
                      <option
                        v-for="tipoContrato in plTipoContratoOptions"
                        :key="`pl_tipo_contrato_${tipoContrato.tipo_contrato}`"
                        :value="tipoContrato.tipo_contrato"
                      >
                        {{ tipoContrato.descripcion }}
                      </option>
                    </select>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.nroContrato }}</small>
                    <select
                      v-model="editDraft.nroContrato"
                      class="form-select form-select-sm inline-input"
                      :disabled="!plDatesReady || !plTerceroOptions.length"
                    >
                      <option value="">{{ labels.selectOption }}</option>
                      <option
                        v-for="contrato in plContratoOptions"
                        :key="`pl_contrato_${contrato.nro_contrato}`"
                        :value="contrato.nro_contrato"
                      >
                        {{ contrato.nro_contrato }}
                      </option>
                    </select>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.tipoActivo }}</small>
                    <select
                      v-model="editDraft.tipoActivo"
                      class="form-select form-select-sm inline-input"
                      :disabled="!plDatesReady"
                    >
                      <option value="">{{ labels.selectOption }}</option>
                      <option
                        v-for="tipoActivo in plTipoActivoOptions"
                        :key="`pl_tipo_activo_${tipoActivo.tipo_activo}`"
                        :value="tipoActivo.tipo_activo"
                      >
                        {{ tipoActivo.descripcion }}
                      </option>
                    </select>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.detalle }}</small>
                    <select
                      v-model="editDraft.detalle"
                      class="form-select form-select-sm inline-input"
                      :disabled="!editDraft.tipoActivo"
                    >
                      <option value="">{{ labels.selectOption }}</option>
                      <option
                        v-for="activo in plDetalleOptions"
                        :key="`pl_detalle_${activo.activo}`"
                        :value="activo.activo"
                      >
                        {{ activo.activo }} - {{ activo.descripcion }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="pl-summary-line" v-if="plContractSelected">
                  {{ labels.tipoActivo }}: {{ plContractSelected.tipo_activo }} |
                  {{ labels.tipoContrato }}: {{ plContractSelected.tipo_contrato }}
                </div>
                <div class="floating-editor-actions pl-actions" @pointerdown.stop>
                  <button
                    class="btn btn-info btn-sm"
                    :disabled="!hasPendingEditChanges(tag.id)"
                    @pointerdown.stop
                    @click="saveTagEdit"
                  >
                    {{ labels.saveChanges }}
                  </button>
                  <button class="btn btn-outline-secondary btn-sm" @pointerdown.stop @click="cancelTagEdit">
                    {{ labels.cancel }}
                  </button>
                </div>
              </div>
              <div v-else class="pl-summary-card" @pointerdown.stop>
                <strong>{{ tag.title }}</strong>
                <div v-if="plGroupedChart(tag).periods.length" class="pl-report-panel">
                  <div class="pl-chart-title">{{ isSpanish ? "Comparación anual" : "Annual comparison" }}</div>
                  <div class="pl-chart">
                    <div class="pl-chart-y-label">{{ labels.value }}</div>
                    <div class="pl-chart-plot">
                      <div class="pl-chart-grid">
                        <span v-for="tick in 5" :key="`${tag.id}_grid_${tick}`" class="pl-chart-grid-line"></span>
                      </div>
                      <div class="pl-chart-categories">
                        <div v-for="category in plGroupedChart(tag).categories" :key="`${tag.id}_cat_${category.key}`" class="pl-category">
                          <div class="pl-bars">
                            <div v-for="bar in category.bars" :key="`${tag.id}_${category.key}_${bar.year}`" class="pl-bar-group">
                              <span class="pl-bar-value">{{ formatPlMoney(bar.value) }}</span>
                              <div class="pl-bar-track">
                                <span class="pl-bar-fill" :style="{ height: `${bar.height}%`, background: bar.color }"></span>
                              </div>
                            </div>
                          </div>
                          <div class="pl-category-label">{{ category.label }}</div>
                        </div>
                      </div>
                    </div>
                    <div class="pl-chart-legend">
                      <span v-for="period in plGroupedChart(tag).periods" :key="`${tag.id}_legend_${period}`" class="pl-legend-item">
                        <i class="pl-legend-dot" :style="{ background: plGroupedChart(tag).periodColors[period] }"></i>
                        {{ period }}
                      </span>
                    </div>
                  </div>
                </div>
                <small v-else class="text-muted">{{ isSpanish ? "Aún no hay datos para mostrar." : "No data available yet." }}</small>
              </div>
            </template>
            <template v-else-if="isComportamientoNota(tag)">
              <div class="pl-note-card" @pointerdown.stop>
                <strong class="pl-note-title">{{ labels.noteTitle || "NOTA A LOS ESTADOS FINANCIEROS" }}</strong>
                <div class="pl-note-columns">
                  <section class="pl-note-section">
                    <h6>ACTIVO POR DERECHO DE USO</h6>
                    <div class="pl-note-row"><span>Saldo inicial</span><strong>{{ formatPlMoney(plNoteSummary(tag).activo.saldoInicial) }}</strong></div>
                    <div class="pl-note-row"><span>+ Nuevos contratos</span><strong>{{ formatPlMoney(plNoteSummary(tag).activo.nuevosContratos) }}</strong></div>
                    <div class="pl-note-row"><span>+ Ajustes al pasivo por arrendamiento</span><strong>{{ formatPlMoney(plNoteSummary(tag).activo.ajustesPasivo) }}</strong></div>
                    <div class="pl-note-row"><span>- Bajas</span><strong>{{ formatPlMoney(plNoteSummary(tag).activo.bajas) }}</strong></div>
                    <div class="pl-note-row total"><span>Saldo final</span><strong>{{ formatPlMoney(plNoteSummary(tag).activo.saldoFinal) }}</strong></div>
                  </section>
                  <section class="pl-note-section">
                    <h6>PASIVO POR ARRENDAMIENTO</h6>
                    <div class="pl-note-row"><span>Saldo inicial</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.saldoInicial) }}</strong></div>
                    <div class="pl-note-row"><span>+ Nuevos contratos</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.nuevosContratos) }}</strong></div>
                    <div class="pl-note-row"><span>+ Ajustes al pasivo por arrendamiento</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.ajustesPasivo) }}</strong></div>
                    <div class="pl-note-row"><span>+ Intereses causados</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.interesesCausados) }}</strong></div>
                    <div class="pl-note-row"><span>- Pagos de intereses</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.pagosIntereses) }}</strong></div>
                    <div class="pl-note-row"><span>- Pagos de capital</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.pagosCapital) }}</strong></div>
                    <div class="pl-note-row"><span>- Bajas</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.bajas) }}</strong></div>
                    <div class="pl-note-row total"><span>Saldo final</span><strong>{{ formatPlMoney(plNoteSummary(tag).pasivo.saldoFinal) }}</strong></div>
                  </section>
                </div>
                <section class="pl-note-section pl-note-section-wide">
                  <h6>AMORTIZACIÓN ACUMULADA</h6>
                  <div class="pl-note-row"><span>Saldo inicial</span><strong>{{ formatPlMoney(plNoteSummary(tag).amortizacion.saldoInicial) }}</strong></div>
                  <div class="pl-note-row"><span>+ Amortización del período</span><strong>{{ formatPlMoney(plNoteSummary(tag).amortizacion.periodo) }}</strong></div>
                  <div class="pl-note-row"><span>- Bajas</span><strong>{{ formatPlMoney(plNoteSummary(tag).amortizacion.bajas) }}</strong></div>
                  <div class="pl-note-row total"><span>Saldo final</span><strong>{{ formatPlMoney(plNoteSummary(tag).amortizacion.saldoFinal) }}</strong></div>
                </section>
              </div>
            </template>
            <template v-else-if="editingTagId === tag.id">
              <div class="inline-editor" @pointerdown.stop>
                <div class="inline-grid">
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.tagTitle }}</small>
                    <input
                      v-model="editDraft.title"
                      class="form-control form-control-sm inline-input"
                      :placeholder="labels.tagTitlePlaceholder"
                    >
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.infoType }}</small>
                    <select v-model="editDraft.infoKey" class="form-select form-select-sm inline-input">
                      <option v-for="opt in infoOptions" :key="`inline_info_${opt.value}`" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.presentationType }}</small>
                    <select v-model="editDraft.presentationType" class="form-select form-select-sm inline-input">
                      <option v-for="opt in editPresentationOptions" :key="`inline_pres_${opt.value}`" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="presentation-preview inline-preview">
                    <span class="presentation-thumb" :class="presentationThumbClass(editDraft.presentationType)"></span>
                    <small>{{ presentationLabel(editDraft) }}</small>
                  </div>
                </div>
                <div class="inline-color-row">
                  <input v-model="editDraft.color" type="color" class="form-control form-control-color">
                  <input v-model="editDraft.textColor" type="color" class="form-control form-control-color">
                </div>
              </div>
            </template>
            <template v-else-if="resolvePresentation(tag) === 'pie'">
              <div class="chart-panel">
                <div v-if="pieComparisons(tag).length" class="dual-pie-grid">
                  <div v-for="pie in pieComparisons(tag)" :key="`${tag.id}_pie_${pie.period}`" class="dual-pie-card">
                    <div class="mini-pie" :style="{ background: pie.gradient }"></div>
                    <strong class="dual-pie-title">{{ pie.period }}</strong>
                    <small class="dual-pie-total">{{ labels.total }}: {{ pie.total }}</small>
                  </div>
                </div>
                <div v-if="pieLegend(tag).length" class="mini-series-legend">
                  <span v-for="item in pieLegend(tag)" :key="`${tag.id}_pie_legend_${item.key}`" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'donut'">
              <div class="chart-panel">
                <div v-if="pieComparisons(tag).length" class="dual-pie-grid">
                  <div v-for="pie in pieComparisons(tag)" :key="`${tag.id}_donut_${pie.period}`" class="dual-pie-card">
                    <div class="mini-donut" :style="{ background: pie.gradient }">
                      <span>{{ pie.total }}</span>
                    </div>
                    <strong class="dual-pie-title">{{ pie.period }}</strong>
                  </div>
                </div>
                <div v-if="pieLegend(tag).length" class="mini-series-legend">
                  <span v-for="item in pieLegend(tag)" :key="`${tag.id}_donut_legend_${item.key}`" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'bars'">
              <div class="chart-panel bars-panel">
                <div class="mini-bars">
                  <div
                    v-for="group in groupedBarItems(tag)"
                    :key="`${tag.id}_bar_group_${group.key}`"
                    class="mini-bar-group"
                    :title="group.tooltip"
                  >
                    <div class="mini-bar-pair">
                      <div
                        v-for="bar in group.bars"
                        :key="`${group.key}_${bar.period}`"
                        class="mini-bar-item"
                      >
                        <div class="mini-bar-top-metrics">
                          <small class="mini-bar-value">{{ bar.value }}</small>
                          <small class="mini-bar-rate">{{ bar.avgRate }}</small>
                        </div>
                        <span
                          class="mini-bar-fill"
                          :style="{ height: `${bar.height}%`, background: bar.color }"
                        />
                      </div>
                    </div>
                    <small class="mini-bar-label">{{ group.shortLabel }}</small>
                  </div>
                </div>
              <div class="tag-legend">{{ labels.value }}: {{ metricDetails(tag).value }} | {{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</div>
              <div class="metric-meta">
                <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                <span>{{ isSpanish ? "Top categoría" : "Top category" }}: {{ rankingItems(tag)[0]?.label || "-" }}</span>
              </div>
            </div>
          </template>

            <template v-else-if="resolvePresentation(tag) === 'bars_horizontal'">
              <div class="chart-panel">
                <div class="mini-hbars">
                  <div v-for="item in horizontalBarItems(tag)" :key="`${tag.id}_hbar_${item.key}`" class="hbar-row">
                    <span class="hbar-label">{{ item.label }}</span>
                    <div class="hbar-track">
                      <span class="hbar-fill" :style="{ width: `${item.width}%`, background: item.color }"></span>
                    </div>
                    <span class="hbar-value">
                      {{ item.value }} ({{ item.percentage }}%)
                      <template v-if="item.avgRate"> | {{ item.avgRate }}</template>
                    </span>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'progress'">
              <div class="chart-panel">
                <div class="progress-track">
                  <span class="progress-fill" :style="progressFillStyle(tag)"></span>
                </div>
                <div class="tag-value kpi-compact">{{ progressPercent(tag) }}%</div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'line'">
              <div class="chart-panel">
                <div class="mini-line">
                  <span
                    v-for="(point, idx) in linePoints(tag)"
                    :key="`${tag.id}_line_${idx}`"
                    class="line-point"
                    :title="pointTitle(point)"
                    :style="{ left: `${point.x}%`, top: `${point.y}%` }"
                  />
                  <svg class="line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polyline :points="linePolyline(tag)" :style="lineStrokeStyle(tag)" />
                  </svg>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'area'">
              <div class="chart-panel">
                <div class="mini-area">
                  <svg class="line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polygon :points="areaPolygon(tag)" :style="areaFillStyle(tag)" />
                    <polyline :points="linePolyline(tag)" :style="lineStrokeStyle(tag)" />
                  </svg>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'gauge'">
              <div class="chart-panel">
                <div class="mini-gauge" :style="gaugeStyle(tag)">
                  <span>{{ progressPercent(tag) }}%</span>
                </div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'ranking'">
              <div class="chart-panel">
                <div class="mini-ranking">
                  <div
                    v-for="item in rankingItems(tag)"
                    :key="`${tag.id}_rank_${item.key}`"
                    class="rank-row"
                    :class="{ selected: item.selected }"
                  >
                    <span class="rank-name">{{ item.label }}</span>
                    <strong class="rank-value">
                      {{ item.value }} · {{ item.percentage }}%
                      <template v-if="item.avgRate"> · {{ item.avgRate }}</template>
                    </strong>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'table'">
              <div class="chart-panel">
                <div class="mini-table">
                  <div class="table-head">
                    <span>{{ tableGroupTitle(tag) }}</span>
                    <span>{{ isSpanish ? "Fecha" : "Period" }}</span>
                    <span>{{ isSpanish ? "Tasa prom." : "Avg rate" }}</span>
                    <span>{{ isSpanish ? "Registros" : "Records" }}</span>
                  </div>
                  <div v-for="item in tableItems(tag)" :key="`${tag.id}_table_${item.key}`" class="table-row real-row">
                    <span>{{ item.dimensionValue }}</span>
                    <strong>{{ item.fechaCierre }}</strong>
                    <strong>{{ item.tasaPromedio }}%</strong>
                    <strong>{{ item.totalRegistros }}</strong>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ isSpanish ? "Filas" : "Rows" }}: {{ tableItems(tag).length }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="tag-value">{{ metricCount(tag) }}</div>
              <div class="tag-legend">{{ metricSummary(tag) }}</div>
              <div class="metric-meta">
                <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
              </div>
            </template>

            <small v-if="editingTagId !== tag.id">{{ labels.source }}: {{ metricLabel(tag) }} | {{ presentationLabel(tag) }}</small>
          </div>

          <div v-if="editingTagId === tag.id" class="floating-editor-actions" @pointerdown.stop>
            <button
              class="btn btn-info btn-sm"
              :disabled="!hasPendingEditChanges(tag.id)"
              @pointerdown.stop
              @click="saveTagEdit"
            >
              {{ labels.saveChanges }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" @pointerdown.stop @click="cancelTagEdit">
              {{ labels.cancel }}
            </button>
          </div>

          <span
            v-if="canManageTags"
            class="resize-handle"
            @pointerdown.stop="startResize($event, tag.id)"
          />
        </article>
      </section>
    </b-container>
  </div>
</template>

<script>
import Menu from "@/services/Menu";
import DashboardEtiquetas from "@/services/DashboardEtiquetas";
import Contrato from "@/services/Contrato";
import Tercero from "@/services/Tercero";
import TipoActivo from "@/services/TipoActivo";
import TipoContrato from "@/services/TipoContrato";
import Activos from "@/services/Activos";

export default {
  name: "List",
  data: () => ({
    maxTags: 50,
    showCreator: false,
    editingTagId: null,
    canManageTags: false,
    tags: [],
    tercerosPl: [],
    tiposActivosPl: [],
    tiposContratosPl: [],
    contratosPl: [],
    activosPl: [],
    plSyncing: false,
    plSummaryCache: {},
    comparativoTasasRows: [],
    comparativoTasasTipoContratoRows: [],
    comparativoTasasTerceroRows: [],
    comparativoFechas: [],
    searchTagId: null,
    periodosDisponibles: [],
    selectedPeriodoActual: "",
    selectedPeriodoComparado: "",
    incluirTodosTerceros: false,
    terceroFilter: "",
    terceroFilterDebounce: null,
    isLoadingMetricas: false,
    activeTagId: null,
    zSeed: 1,
    interaction: null,
    draft: {
      title: "",
      infoKey: "comportamiento_pl",
      presentationType: "graficos",
      color: "#f5faff",
      textColor: "#000000",
    },
    editDraft: {
      title: "",
      infoKey: "comportamiento_pl",
      presentationType: "graficos",
      color: "#f5faff",
      textColor: "#000000",
      fechaInicial: "",
      fechaFinal: "",
      arrendador: "",
      tipoContrato: "",
      nroContrato: "",
      tipoActivo: "",
      detalle: "",
    },
    editOriginal: null,
  }),
  computed: {
    isSpanish() {
      return this.$store.state._language == "es-ES";
    },
    isLimitReached() {
      const requiredTags = this.draft?.infoKey === "comportamiento_pl" ? 2 : 1;
      return this.tags.length + requiredTags > this.maxTags;
    },
    isEditingAnyTag() {
      return this.editingTagId !== null;
    },
    labels() {
      if (this.isSpanish) {
        return {
          welcomeTitle: "Bienvenido a Arrendamiento",
          createTag: "Crear etiqueta",
          creatorTitle: "Nueva etiqueta",
          tagTitle: "Título",
          tagTitlePlaceholder: "Ejemplo: Estado reportes activos",
          infoType: "Qué información quieres ver",
          presentationType: "Cómo presentar la etiqueta",
          background: "Color fondo",
          textColor: "Color texto",
          editTitle: "Editar etiqueta",
          saveChanges: "Guardar cambios",
          saveTag: "Guardar etiqueta",
          cancel: "Cancelar",
          selectOption: "Seleccione...",
          fechaInicial: "Fecha inicial",
          fechaFinal: "Fecha final",
          arrendador: "Arrendador",
          tipoContrato: "Tipo de contrato",
          nroContrato: "Número de contrato",
          tipoActivo: "Tipo de activo",
          detalle: "Detalle",
          gastoAmortizacion: "Gasto por amortización",
          gastoIntereses: "Gasto por intereses",
          canonArrendamiento: "Canon de arrendamiento",
          period: "Periodo",
          noteTitle: "NOTA A LOS ESTADOS FINANCIEROS",
          emptyState: "No hay etiquetas todavía. Crea la primera para empezar.",
          source: "Fuente",
          totalReports: "Total reportes",
          total: "Total",
          value: "Valor",
          percentage: "Porcentaje",
          otherReports: "Resto",
          tagCounter: (count, limit) => `Etiquetas: ${count}/${limit}`,
          fallbackTagTitle: "Nueva etiqueta",
          noSpace: "No hay espacio disponible para otra etiqueta sin superponer.",
          limitReached: "No se pueden crear mas etiquetas.",
          editCollision: "No se puede editar: la etiqueta se superpone con otra.",
          readOnly: "Solo lectura",
          noPermission: "No tienes permiso para modificar etiquetas.",
          confirmDeleteTag: "¿Deseas eliminar esta etiqueta?",
          noValue: "Sin dato",
        };
      }
      return {
        welcomeTitle: "Welcome to Leasing",
        createTag: "Create tag",
        creatorTitle: "New tag",
        tagTitle: "Title",
        tagTitlePlaceholder: "Example: Active reports status",
        infoType: "What data do you want",
        presentationType: "How to present the tag",
        background: "Background color",
        textColor: "Text color",
        editTitle: "Edit tag",
        saveChanges: "Save changes",
        saveTag: "Save tag",
        cancel: "Cancel",
        selectOption: "Select...",
        fechaInicial: "Start date",
        fechaFinal: "End date",
        arrendador: "Lessor",
        tipoContrato: "Contract type",
        nroContrato: "Contract number",
        tipoActivo: "Asset type",
        detalle: "Detail",
        gastoAmortizacion: "Amortization expense",
        gastoIntereses: "Interest expense",
        canonArrendamiento: "Lease canon",
        period: "Period",
        noteTitle: "NOTE TO THE FINANCIAL STATEMENTS",
        emptyState: "No tags yet. Create the first one to start.",
        source: "Source",
        totalReports: "Total reports",
        total: "Total",
        value: "Value",
        percentage: "Percentage",
        otherReports: "Others",
        tagCounter: (count, limit) => `Tags: ${count}/${limit}`,
        fallbackTagTitle: "New tag",
        noSpace: "There is no free space for another tag without overlap.",
        limitReached: "No more tags can be created.",
        editCollision: "Cannot edit: the tag overlaps another one.",
        readOnly: "Read only",
        noPermission: "You do not have permission to modify tags.",
        confirmDeleteTag: "Do you want to delete this tag?",
        noValue: "No data",
      };
    },
    infoOptions() {
      return [{ value: "comportamiento_pl", label: this.isSpanish ? "COMPORTAMIENTO P&L" : "P&L BEHAVIOR" }];
    },
    presentationOptions() {
      if (this.isSpanish) {
        return [
          { value: "value", label: "Valor numérico" },
          { value: "bars", label: "Gráfico de barras" },
          { value: "bars_horizontal", label: "Barras horizontales" },
          { value: "line", label: "Gráfico de línea" },
          { value: "area", label: "Gráfico de área" },
          { value: "pie", label: "Gráfico de pastel" },
          { value: "donut", label: "Gráfico de dona" },
          { value: "gauge", label: "Medidor" },
          { value: "progress", label: "Indicador de progreso" },
          { value: "ranking", label: "Ranking" },
          { value: "table", label: "Tabla comparativa" },
        ];
      }
      return [
        { value: "value", label: "Numeric value" },
        { value: "bars", label: "Bar chart" },
        { value: "bars_horizontal", label: "Horizontal bars" },
        { value: "line", label: "Line chart" },
        { value: "area", label: "Area chart" },
        { value: "pie", label: "Pie chart" },
        { value: "donut", label: "Donut chart" },
        { value: "gauge", label: "Gauge" },
        { value: "progress", label: "Progress indicator" },
        { value: "ranking", label: "Ranking" },
        { value: "table", label: "Comparison table" },
      ];
    },
    creatorPresentationOptions() {
      return this.presentationOptionsByInfoKey(this.draft.infoKey);
    },
    editPresentationOptions() {
      return this.presentationOptionsByInfoKey(this.editDraft.infoKey);
    },
    plDatesReady() {
      return Boolean(this.editDraft.fechaInicial && this.editDraft.fechaFinal);
    },
    plTerceroOptions() {
      return this.tercerosPl
        .slice()
        .sort((left, right) => String(left.nombre || "").localeCompare(String(right.nombre || "")));
    },
    plTipoContratoOptions() {
      return this.tiposContratosPl
        .slice()
        .sort((left, right) => Number(left.tipo_contrato) - Number(right.tipo_contrato));
    },
    plTipoActivoOptions() {
      return this.tiposActivosPl
        .slice()
        .sort((left, right) => Number(left.tipo_activo) - Number(right.tipo_activo));
    },
    plContratoOptions() {
      const filtered = this.contratosPl.filter((contrato) => {
        if (this.editDraft.arrendador && String(contrato.identificacion) !== String(this.editDraft.arrendador)) return false;
        if (this.editDraft.tipoContrato && String(contrato.tipo_contrato) !== String(this.editDraft.tipoContrato)) return false;
        if (this.editDraft.tipoActivo && String(contrato.tipo_activo) !== String(this.editDraft.tipoActivo)) return false;
        return true;
      });
      const uniqueByContract = new Map();
      filtered.forEach((contrato) => {
        const key = String(contrato.nro_contrato || "");
        if (!uniqueByContract.has(key)) {
          uniqueByContract.set(key, contrato);
        }
      });
      return Array.from(uniqueByContract.values());
    },
    plDetalleOptions() {
      return this.activosPl.filter((activo) => {
        if (this.editDraft.tipoActivo && String(activo.tipo_activo) !== String(this.editDraft.tipoActivo)) return false;
        return true;
      });
    },
    plContractSelected() {
      const contrato = this.plContratoOptions.find((item) => String(item.nro_contrato) === String(this.editDraft.nroContrato));
      if (!contrato) return null;
      return contrato;
    },
  },
  watch: {
    "draft.infoKey"() {
      this.ensureValidPresentationSelection("draft");
    },
    "editDraft.infoKey"() {
      this.ensureValidPresentationSelection("edit");
    },
    "editDraft.fechaInicial"() {
      this.onPlFechaChange();
    },
    "editDraft.fechaFinal"() {
      this.onPlFechaChange();
    },
    "editDraft.arrendador"() {
      this.onPlArrendadorChange();
    },
    "editDraft.tipoContrato"() {
      this.onPlTipoContratoChange();
    },
    "editDraft.nroContrato"() {
      this.onPlContratoChange();
    },
    "editDraft.tipoActivo"() {
      this.onPlTipoActivoChange();
    },
  },
  methods: {
    buildComparativoGroups(infoKey, limit = 5) {
      const rows = this.getRowsByInfoKey(infoKey);
      const rowPeriods = [...new Set(rows.map((row) => String(row?.fecha_cierre || "")))]
        .filter(Boolean)
        .sort()
        .reverse();
      const periodCandidates = this.comparativoFechas
        .filter((period) => rowPeriods.includes(period));
      const periods = (periodCandidates.length ? periodCandidates : rowPeriods).slice(0, 2);
      const grouped = {};

      rows.forEach((row) => {
        const dimension = this.dimensionValueFromRow(row, infoKey);
        const period = String(row?.fecha_cierre || "");
        if (!grouped[dimension]) grouped[dimension] = {};
        grouped[dimension][period] = {
          registros: Number(row?.total_registros) || 0,
          tasa: Number(row?.tasa) || 0,
        };
      });

      return Object.entries(grouped)
        .map(([label, byPeriod]) => {
          const total = periods.reduce((acc, period) => {
            const periodData = byPeriod[period] || {};
            return acc + (Number(periodData.registros) || 0);
          }, 0);
          return { label, byPeriod, total };
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, limit)
        .map((group, index) => ({
          key: `${group.label}_${index}`,
          label: group.label,
          shortLabel: String(group.label || "").slice(0, 16),
          periods,
          bars: periods.map((period) => {
            const periodData = group.byPeriod[period] || {};
            return {
              period,
              periodShort: period.slice(2, 4),
              value: Number(periodData.registros) || 0,
              avgRate: `${Number(periodData.tasa || 0).toFixed(4)}%`,
            };
          }),
        }));
    },
    presentationOptionsByInfoKey(infoKey) {
      if (infoKey === "comportamiento_pl") {
        return [{ value: "graficos", label: "graficos" }];
      }
      if (infoKey === "comportamiento_pl_nota") {
        return [{ value: "nota", label: "nota" }];
      }
      return this.presentationOptions;
    },
    ensureValidPresentationSelection(target = "draft") {
      const source = target === "edit" ? this.editDraft : this.draft;
      if (!source) return;
      const available = this.presentationOptionsByInfoKey(source.infoKey);
      if (!available.length) return;
      if (!available.some((opt) => opt.value === source.presentationType)) {
        source.presentationType = available[0].value;
      }
    },
    isComportamientoPl(tag) {
      return this.resolveInfoKey(tag) === "comportamiento_pl";
    },
    isComportamientoNota(tag) {
      return this.resolveInfoKey(tag) === "comportamiento_pl_nota";
    },
    isComparativoTercero() {
      return false;
    },
    buildPlDraftFromTag(tag) {
      return {
        fechaInicial: tag.fechaInicial || "",
        fechaFinal: tag.fechaFinal || "",
        arrendador: tag.arrendador || "",
        tipoContrato: tag.tipoContrato || "",
        nroContrato: tag.nroContrato || "",
        tipoActivo: tag.tipoActivo || "",
        detalle: tag.detalle || "",
      };
    },
    buildPlNoteSummaryFromRows(rows, tag) {
      const orderedRows = rows
        .slice()
        .sort((left, right) => String(left?.fecha_cierre || "").localeCompare(String(right?.fecha_cierre || "")));
      const firstRow = orderedRows[0] || {};
      const lastRow = orderedRows[orderedRows.length - 1] || {};
      const initialPeriod = String(tag?.fechaInicial || "").slice(0, 7);
      const firstPeriod = String(firstRow?.fecha_cierre || "");
      const enteredAtStart = firstPeriod === initialPeriod;
      const sumField = (field) => rows.reduce((accumulator, row) => accumulator + (Number(row?.[field]) || 0), 0);
      const diffField = (field) => (Number(lastRow?.[field]) || 0) - (Number(firstRow?.[field]) || 0);
      const activeSaldoInicial = enteredAtStart ? Number(firstRow?.activo_derecho_uso_bruto) || 0 : 0;
      const activeNuevosContratos = enteredAtStart ? 0 : Number(firstRow?.activo_derecho_uso_bruto) || 0;
      const activeAjustes = sumField("total_cambio_pasivo");
      const activeBajas = sumField("valor_baja");
      const activeSaldoFinal = activeSaldoInicial + activeNuevosContratos + activeAjustes - activeBajas;

      const passiveSaldoInicial = enteredAtStart ? Number(firstRow?.pasivo_inicial) || 0 : 0;
      const passiveNuevosContratos = enteredAtStart ? 0 : Number(firstRow?.pasivo_inicial) || 0;
      const passiveAjustes = diffField("total_cambio_pasivo");
      const passiveIntereses = diffField("total_intereses");
      const passivePagosIntereses = 0;
      const passivePagosCapital = diffField("total_pago_capital");
      const passiveBajas = sumField("valor_baja");
      const passiveSaldoFinal = passiveSaldoInicial + passiveNuevosContratos + passiveAjustes + passiveIntereses - passivePagosIntereses - passivePagosCapital - passiveBajas;

      const amortizacionSaldoInicial = enteredAtStart ? Number(firstRow?.amortizacion_acumulada) || 0 : 0;
      const amortizacionPeriodo = diffField("total_gasto_amortizacion");
      const amortizacionBajas = 0;
      const amortizacionSaldoFinal = amortizacionSaldoInicial + amortizacionPeriodo - amortizacionBajas;

      return {
        activo: {
          saldoInicial: activeSaldoInicial,
          nuevosContratos: activeNuevosContratos,
          ajustesPasivo: activeAjustes,
          bajas: activeBajas,
          saldoFinal: activeSaldoFinal,
        },
        pasivo: {
          saldoInicial: passiveSaldoInicial,
          nuevosContratos: passiveNuevosContratos,
          ajustesPasivo: passiveAjustes,
          interesesCausados: passiveIntereses,
          pagosIntereses: passivePagosIntereses,
          pagosCapital: passivePagosCapital,
          bajas: passiveBajas,
          saldoFinal: passiveSaldoFinal,
        },
        amortizacion: {
          saldoInicial: amortizacionSaldoInicial,
          periodo: amortizacionPeriodo,
          bajas: amortizacionBajas,
          saldoFinal: amortizacionSaldoFinal,
        },
      };
    },
    hasPlDataReady() {
      return Boolean(
        this.editDraft.fechaInicial &&
        this.editDraft.fechaFinal &&
        this.editDraft.arrendador &&
        this.editDraft.tipoContrato &&
        this.editDraft.nroContrato &&
        this.editDraft.tipoActivo
      );
    },
    formatPlMoney(value) {
      return new Intl.NumberFormat("es-CO", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    formatPlValue(value) {
      if (value === null || value === undefined || value === "") return "-";
      return String(value);
    },
    formatPlTercero(value) {
      const tercero = this.tercerosPl.find((item) => String(item.identificacion) === String(value));
      if (!tercero) return this.formatPlValue(value);
      return `${tercero.nombre} (${tercero.identificacion})`;
    },
    formatPlTipoContrato(value) {
      const tipoContrato = this.tiposContratosPl.find((item) => String(item.tipo_contrato) === String(value));
      if (!tipoContrato) return this.formatPlValue(value);
      return tipoContrato.descripcion;
    },
    formatPlTipoActivo(value) {
      const tipoActivo = this.tiposActivosPl.find((item) => String(item.tipo_activo) === String(value));
      if (!tipoActivo) return this.formatPlValue(value);
      return tipoActivo.descripcion;
    },
    formatPlActivo(value) {
      const activo = this.activosPl.find((item) => String(item.activo) === String(value));
      if (!activo) return this.formatPlValue(value);
      return `${activo.activo} - ${activo.descripcion}`;
    },
    async loadPlDependencies() {
      try {
        const [tercerosRes, tiposActivosRes, tiposContratosRes] = await Promise.allSettled([
          Tercero.getAllTercero(),
          TipoActivo.getAllTipoActivo(),
          TipoContrato.getAllTipoContrato(),
        ]);

        this.tercerosPl = tercerosRes.status === "fulfilled" ? (tercerosRes.value?.data?.listTercero || []) : [];
        this.tiposActivosPl = tiposActivosRes.status === "fulfilled" ? (tiposActivosRes.value?.data?.listTipoActivo || []) : [];
        this.tiposContratosPl = tiposContratosRes.status === "fulfilled" ? (tiposContratosRes.value?.data?.listTipoContrato || []) : [];
      } catch (error) {
        this.tercerosPl = [];
        this.tiposActivosPl = [];
        this.tiposContratosPl = [];
      }
    },
    async loadPlContracts() {
      if (!this.editDraft.fechaInicial || !this.editDraft.fechaFinal) {
        this.contratosPl = [];
        return;
      }
      try {
        const res = await Contrato.getContratosByFechaInicio_FechaFin(this.editDraft.fechaInicial, this.editDraft.fechaFinal);
        this.contratosPl = Array.isArray(res?.data?.qryContratos) ? res.data.qryContratos : [];
      } catch (error) {
        this.contratosPl = [];
      }
    },
    plSourceFromTag(tag) {
      return {
        fechaInicial: tag.fechaInicial || "",
        fechaFinal: tag.fechaFinal || "",
        arrendador: tag.arrendador || "",
        tipoContrato: tag.tipoContrato || "",
        nroContrato: tag.nroContrato || "",
        tipoActivo: tag.tipoActivo || "",
        detalle: tag.detalle || "",
      };
    },
    filterPlRows(rows, source) {
      return rows.filter((row) => {
        if (source.arrendador && String(row.identificacion) !== String(source.arrendador)) return false;
        if (source.tipoContrato && String(row.tipo_contrato) !== String(source.tipoContrato)) return false;
        if (source.nroContrato && String(row.nro_contrato) !== String(source.nroContrato)) return false;
        if (source.tipoActivo && String(row.tipo_activo) !== String(source.tipoActivo)) return false;
        return true;
      });
    },
    buildPlSummaryFromRows(rows, tag) {
      const periodTotals = {};
      const selectedPeriods = [];
      const initialPeriod = String(tag?.fechaInicial || "").slice(0, 7);
      const finalPeriod = String(tag?.fechaFinal || "").slice(0, 7);
      [initialPeriod, finalPeriod].forEach((period) => {
        if (period && !selectedPeriods.includes(period)) selectedPeriods.push(period);
      });
      rows.forEach((row) => {
        const period = String(row?.fecha_cierre || "").slice(0, 7);
        if (!period) return;
        if (!periodTotals[period]) {
          periodTotals[period] = {
            period,
            gastoAmortizacion: 0,
            gastoIntereses: 0,
            canonArrendamiento: 0,
            rows: 0,
          };
        }
        periodTotals[period].gastoAmortizacion += Number(row?.total_gasto_amortizacion) || 0;
        periodTotals[period].gastoIntereses += Number(row?.total_intereses) || 0;
        periodTotals[period].canonArrendamiento += Number(row?.total_valor_canon) || 0;
        periodTotals[period].rows += 1;
      });

      const periods = selectedPeriods.length ? selectedPeriods : Object.keys(periodTotals).sort().reverse();
      const metrics = [
        { key: "gastoAmortizacion", label: this.labels.gastoAmortizacion },
        { key: "gastoIntereses", label: this.labels.gastoIntereses },
        { key: "canonArrendamiento", label: this.labels.canonArrendamiento },
      ].map((metric) => {
        const points = periods.map((period) => ({
          period,
          value: periodTotals[period]?.[metric.key] || 0,
        }));
        const maxValue = Math.max(...points.map((item) => item.value), 1);
        return {
          ...metric,
          points: points.map((item) => ({
            ...item,
            height: Math.max(Math.round((item.value / maxValue) * 100), item.value > 0 ? 18 : 8),
          })),
        };
      });

      return { periods, periodTotals, metrics };
    },
    async refreshPlSummaryForTag(tag) {
      if (!tag || !tag.id || (!this.isComportamientoPl(tag) && !this.isComportamientoNota(tag)) || !tag.fechaInicial || !tag.fechaFinal) return;
      try {
        const res = await Contrato.getContratosByFechaInicio_FechaFin(tag.fechaInicial, tag.fechaFinal);
        const rows = Array.isArray(res?.data?.qryContratos) ? res.data.qryContratos : [];
        const filteredRows = this.filterPlRows(rows, this.plSourceFromTag(tag));
        this.plSummaryCache = {
          ...this.plSummaryCache,
          [tag.id]: {
            ...this.buildPlSummaryFromRows(filteredRows, tag),
            rows: filteredRows,
            note: this.buildPlNoteSummaryFromRows(filteredRows, tag),
          },
        };
      } catch (error) {
        this.plSummaryCache = {
          ...this.plSummaryCache,
          [tag.id]: { periods: [], periodTotals: {}, metrics: [], rows: [], note: null },
        };
      }
    },
    async refreshPlSummaries() {
      const plTags = this.tags.filter((tag) => (this.isComportamientoPl(tag) || this.isComportamientoNota(tag)) && tag.fechaInicial && tag.fechaFinal);
      await Promise.all(plTags.map((tag) => this.refreshPlSummaryForTag(tag)));
    },
    plSummaryForTag(tag) {
      return this.plSummaryCache[tag?.id] || { periods: [], periodTotals: {}, metrics: [], rows: [], note: null };
    },
    plMetricCards(tag) {
      return this.plSummaryForTag(tag).metrics || [];
    },
    plTableRows(tag) {
      const summary = this.plSummaryForTag(tag);
      const periods = this.plComparisonPeriods(tag, summary.periods || []);
      return periods.map((period) => ({
        period,
        gastoAmortizacion: summary.periodTotals?.[period]?.gastoAmortizacion || 0,
        gastoIntereses: summary.periodTotals?.[period]?.gastoIntereses || 0,
        canonArrendamiento: summary.periodTotals?.[period]?.canonArrendamiento || 0,
        rows: summary.periodTotals?.[period]?.rows || 0,
      }));
    },
    plGroupedChart(tag) {
      const summary = this.plSummaryForTag(tag);
      const periods = this.plComparisonPeriods(tag, summary.periods || []);
      const yearPalette = [
        "hsl(2, 54%, 54%)",
        "hsl(215, 54%, 55%)",
      ];
      const periodColors = periods.reduce((accumulator, period, index) => {
        accumulator[period] = yearPalette[index % yearPalette.length];
        return accumulator;
      }, {});
      const allValues = [];
      const categories = [
        { key: "gastoAmortizacion", label: this.labels.gastoAmortizacion },
        { key: "gastoIntereses", label: this.labels.gastoIntereses },
        { key: "canonArrendamiento", label: this.labels.canonArrendamiento },
      ].map((category) => {
        const bars = periods.map((period) => {
          const value = Number(summary.periodTotals?.[period]?.[category.key] || 0);
          allValues.push(value);
          return {
            period,
            value,
            color: periodColors[period],
          };
        });
        return { ...category, bars };
      });
      const maxValue = Math.max(...allValues, 1);
      return {
        periods,
        periodColors,
        categories: categories.map((category) => ({
          ...category,
          bars: category.bars.map((bar) => ({
            ...bar,
            height: Math.max(Math.round((bar.value / maxValue) * 100), bar.value > 0 ? 10 : 4),
          })),
        })),
      };
    },
    plNoteSummary(tag) {
      return this.plSummaryForTag(tag).note || {
        activo: {
          saldoInicial: 0,
          nuevosContratos: 0,
          ajustesPasivo: 0,
          bajas: 0,
          saldoFinal: 0,
        },
        pasivo: {
          saldoInicial: 0,
          nuevosContratos: 0,
          ajustesPasivo: 0,
          interesesCausados: 0,
          pagosIntereses: 0,
          pagosCapital: 0,
          bajas: 0,
          saldoFinal: 0,
        },
        amortizacion: {
          saldoInicial: 0,
          periodo: 0,
          bajas: 0,
          saldoFinal: 0,
        },
      };
    },
    plComparisonPeriods(tag, fallbackPeriods = []) {
      const initialPeriod = String(tag?.fechaInicial || "").slice(0, 7);
      const finalPeriod = String(tag?.fechaFinal || "").slice(0, 7);
      const preferredPeriods = [initialPeriod, finalPeriod].filter((period, index, array) => period && array.indexOf(period) === index);
      if (preferredPeriods.length) {
        return preferredPeriods;
      }
      return fallbackPeriods;
    },
    async loadPlActivos() {
      if (!this.editDraft.tipoActivo) {
        this.activosPl = [];
        return;
      }
      try {
        const res = await Activos.getActivoByTipoActivo(this.editDraft.tipoActivo);
        this.activosPl = Array.isArray(res?.data?.activoByTipoAct) ? res.data.activoByTipoAct : [];
      } catch (error) {
        this.activosPl = [];
      }
    },
    async onPlFechaChange() {
      if (this.plSyncing) return;
      if (!this.isComportamientoPl(this.tags.find((item) => item.id === this.editingTagId) || {})) return;
      this.editDraft.arrendador = "";
      this.editDraft.tipoContrato = "";
      this.editDraft.nroContrato = "";
      this.editDraft.tipoActivo = "";
      this.editDraft.detalle = "";
      await this.loadPlContracts();
      this.activosPl = [];
    },
    onPlArrendadorChange() {
      if (this.plSyncing) return;
      this.editDraft.tipoContrato = "";
      this.editDraft.nroContrato = "";
      this.editDraft.tipoActivo = "";
      this.editDraft.detalle = "";
    },
    onPlTipoContratoChange() {
      if (this.plSyncing) return;
      if (this.editDraft.nroContrato) {
        const selectedContract = this.plContractSelected;
        if (selectedContract && String(selectedContract.tipo_contrato) !== String(this.editDraft.tipoContrato)) {
          this.editDraft.nroContrato = "";
        }
      }
    },
    async onPlContratoChange() {
      if (this.plSyncing) return;
      const selectedContract = this.plContractSelected;
      if (!selectedContract) return;
      this.plSyncing = true;
      try {
        this.editDraft.arrendador = selectedContract.identificacion || "";
        this.editDraft.tipoContrato = selectedContract.tipo_contrato || "";
        this.editDraft.tipoActivo = selectedContract.tipo_activo || "";
        await this.loadPlActivos();
      } finally {
        this.plSyncing = false;
      }
    },
    async onPlTipoActivoChange() {
      if (this.plSyncing) return;
      if (!this.editDraft.tipoActivo) {
        this.editDraft.detalle = "";
        this.activosPl = [];
        return;
      }
      await this.loadPlActivos();
      const selectedContract = this.plContractSelected;
      if (selectedContract && String(selectedContract.tipo_activo) !== String(this.editDraft.tipoActivo)) {
        this.editDraft.nroContrato = "";
      }
      if (this.editDraft.detalle) {
        const stillExists = this.plDetalleOptions.some((item) => String(item.activo) === String(this.editDraft.detalle));
        if (!stillExists) this.editDraft.detalle = "";
      }
    },
    async onPlEditOpen(tag) {
      try {
        this.editDraft = {
          ...this.editDraft,
          ...this.buildPlDraftFromTag(tag),
        };
        await this.loadPlContracts();
        if (this.editDraft.tipoActivo) {
          await this.loadPlActivos();
        }
      } finally {
        this.plSyncing = false;
      }
    },
    getTagMinSize(tagLike) {
      const presentation = this.resolvePresentation(tagLike);
      if (this.resolveInfoKey(tagLike) === "comportamiento_pl" || presentation === "graficos") {
        return { minWidth: 340, minHeight: 230 };
      }
      if (this.resolveInfoKey(tagLike) === "comportamiento_pl_nota" || presentation === "nota") {
        return { minWidth: 460, minHeight: 280 };
      }
      if (presentation === "bars") return { minWidth: 260, minHeight: 175 };
      if (presentation === "bars_horizontal") return { minWidth: 280, minHeight: 190 };
      if (presentation === "line" || presentation === "area") return { minWidth: 280, minHeight: 190 };
      if (presentation === "pie") return { minWidth: 240, minHeight: 175 };
      if (presentation === "donut") return { minWidth: 250, minHeight: 185 };
      if (presentation === "gauge") return { minWidth: 250, minHeight: 175 };
      if (presentation === "progress") return { minWidth: 240, minHeight: 160 };
      if (presentation === "ranking" || presentation === "table") return { minWidth: 420, minHeight: 220 };
      return { minWidth: 190, minHeight: 130 };
    },
    toggleCreator() {
      if (!this.ensureCanManageTags()) return;
      this.showCreator = !this.showCreator;
      if (this.showCreator) this.cancelTagEdit();
    },
    ensureCanManageTags(notify = true) {
      if (this.canManageTags) return true;
      if (notify && this.$toast?.warning) {
        this.$toast.warning(this.labels.noPermission);
      }
      return false;
    },
    notifyLimitReached() {
      if (this.$toast?.warning) {
        this.$toast.warning(this.labels.limitReached);
      }
    },
    handleCreateClick() {
      if (!this.ensureCanManageTags()) return;
      if (this.isLimitReached) {
        this.notifyLimitReached();
        return;
      }
      this.toggleCreator();
    },
    async openTagEditor(tagId) {
      if (!this.ensureCanManageTags()) return;
      if (this.editingTagId === tagId) {
        this.cancelTagEdit();
        return;
      }
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      if (this.isComportamientoNota(tag)) return;
      this.editingTagId = tagId;
      if (this.isComportamientoPl(tag)) {
        this.plSyncing = true;
      }
      this.editDraft = {
        title: tag.title || "",
        infoKey: this.resolveInfoKey(tag),
        presentationType: this.resolvePresentation(tag),
        color: tag.color || "#f5faff",
        textColor: tag.textColor || "#000000",
        ...this.buildPlDraftFromTag(tag),
      };
      this.ensureValidPresentationSelection("edit");
      this.editOriginal = { ...this.editDraft };
      this.showCreator = false;
      if (this.isComportamientoPl(tag)) {
        await this.onPlEditOpen(tag);
      }
    },
    cancelTagEdit() {
      this.editingTagId = null;
      this.editOriginal = null;
      this.plSyncing = false;
    },
    hasPendingEditChanges(tagId = null) {
      if (!this.editOriginal || this.editingTagId === null) return false;
      if (tagId !== null && tagId !== this.editingTagId) return false;

      const current = {
        title: (this.editDraft.title || "").trim(),
        infoKey: this.editDraft.infoKey,
        presentationType: this.editDraft.presentationType,
        color: this.editDraft.color,
        textColor: this.editDraft.textColor,
        fechaInicial: this.editDraft.fechaInicial,
        fechaFinal: this.editDraft.fechaFinal,
        arrendador: String(this.editDraft.arrendador || ""),
        tipoContrato: String(this.editDraft.tipoContrato || ""),
        nroContrato: String(this.editDraft.nroContrato || ""),
        tipoActivo: String(this.editDraft.tipoActivo || ""),
        detalle: String(this.editDraft.detalle || ""),
      };
      const original = {
        title: (this.editOriginal.title || "").trim(),
        infoKey: this.editOriginal.infoKey,
        presentationType: this.editOriginal.presentationType,
        color: this.editOriginal.color,
        textColor: this.editOriginal.textColor,
        fechaInicial: this.editOriginal.fechaInicial,
        fechaFinal: this.editOriginal.fechaFinal,
        arrendador: String(this.editOriginal.arrendador || ""),
        tipoContrato: String(this.editOriginal.tipoContrato || ""),
        nroContrato: String(this.editOriginal.nroContrato || ""),
        tipoActivo: String(this.editOriginal.tipoActivo || ""),
        detalle: String(this.editOriginal.detalle || ""),
      };

      return (
        current.title !== original.title ||
        current.infoKey !== original.infoKey ||
        current.presentationType !== original.presentationType ||
        current.color !== original.color ||
        current.textColor !== original.textColor ||
        current.fechaInicial !== original.fechaInicial ||
        current.fechaFinal !== original.fechaFinal ||
        current.arrendador !== original.arrendador ||
        current.tipoContrato !== original.tipoContrato ||
        current.nroContrato !== original.nroContrato ||
        current.tipoActivo !== original.tipoActivo ||
        current.detalle !== original.detalle
      );
    },
    buildEtiquetaPayload(tag) {
      return {
        id: tag.id,
        idUser: this.$store.state.userId,
        titulo: tag.title,
        info_key: tag.infoKey,
        presentation_type: tag.presentationType,
        pl_fecha_inicial: tag.fechaInicial || "",
        pl_fecha_final: tag.fechaFinal || "",
        pl_arrendador: tag.arrendador || "",
        pl_tipo_contrato: tag.tipoContrato || "",
        pl_num_contrato: tag.nroContrato || "",
        pl_tipo_activo: tag.tipoActivo || "",
        pl_detalle: tag.detalle || "",
        pos_x: tag.left,
        pos_y: tag.top,
        width_px: tag.width,
        height_px: tag.height,
        color_fondo: tag.color,
        color_texto: tag.textColor,
        z_index: tag.zIndex,
      };
    },
    async persistTag(tag) {
      if (!tag?.id) return;
      try {
        await DashboardEtiquetas.update(this.buildEtiquetaPayload(tag));
      } catch (error) {
        this.$toast?.error?.("No se pudo guardar la etiqueta en base de datos.");
      }
    },
    async saveTagEdit() {
      if (!this.ensureCanManageTags()) return;
      const tag = this.tags.find((item) => item.id === this.editingTagId);
      if (!tag) {
        this.cancelTagEdit();
        return;
      }
      if (this.isComportamientoPl(tag) && !this.hasPlDataReady()) {
        if (this.$toast?.warning) this.$toast.warning("Completa las fechas, el arrendador y el contrato antes de guardar.");
        return;
      }
      if (!this.hasPendingEditChanges()) {
        this.cancelTagEdit();
        return;
      }

      const minSize = this.getTagMinSize(this.editDraft);
      const nextWidth = Math.max(tag.width, minSize.minWidth);
      const nextHeight = Math.max(tag.height, minSize.minHeight);
      const candidate = this.toRect(tag, { width: nextWidth, height: nextHeight });
      if (this.hasCollision(candidate, tag.id)) {
        if (this.$toast?.warning) this.$toast.warning(this.labels.editCollision);
        return;
      }

      tag.title = this.editDraft.title?.trim() || this.labels.fallbackTagTitle;
      tag.infoKey = this.editDraft.infoKey;
      tag.presentationType = this.editDraft.presentationType;
      tag.color = this.editDraft.color || "#f5faff";
      tag.textColor = this.editDraft.textColor || "#000000";
      tag.fechaInicial = this.editDraft.fechaInicial || "";
      tag.fechaFinal = this.editDraft.fechaFinal || "";
      tag.arrendador = this.editDraft.arrendador || "";
      tag.tipoContrato = this.editDraft.tipoContrato || "";
      tag.nroContrato = this.editDraft.nroContrato || "";
      tag.tipoActivo = this.editDraft.tipoActivo || "";
      tag.detalle = this.editDraft.detalle || "";
      tag.width = nextWidth;
      tag.height = nextHeight;
      await this.persistTag(tag);
      await this.syncComportamientoNotaFromTag(tag);
      await this.refreshPlSummaryForTag(tag);
      this.cancelTagEdit();
    },
    findComportamientoNotaForTag(tag) {
      if (!tag?.id) return null;
      return this.tags.find((item) => this.isComportamientoNota(item) && String(item.detalle) === String(tag.id)) || null;
    },
    async syncComportamientoNotaFromTag(tag) {
      if (!this.isComportamientoPl(tag) || !tag?.id) return;
      const noteSize = this.getTagMinSize({ infoKey: "comportamiento_pl_nota", presentationType: "nota" });
      const noteWidth = Math.max(260, noteSize.minWidth);
      const noteHeight = Math.max(180, noteSize.minHeight);
      const noteTitle = this.labels.noteTitle || "NOTA A LOS ESTADOS FINANCIEROS";
      const noteTag = this.findComportamientoNotaForTag(tag);

      if (!noteTag) {
        const notePosition = this.findAvailablePosition(noteWidth, noteHeight);
        if (!notePosition) return;
        const newNoteTag = {
          id: null,
          title: noteTitle,
          infoKey: "comportamiento_pl_nota",
          presentationType: "nota",
          fechaInicial: tag.fechaInicial || "",
          fechaFinal: tag.fechaFinal || "",
          arrendador: tag.arrendador || "",
          tipoContrato: tag.tipoContrato || "",
          nroContrato: tag.nroContrato || "",
          tipoActivo: tag.tipoActivo || "",
          detalle: String(tag.id),
          width: noteWidth,
          height: noteHeight,
          left: notePosition.left,
          top: notePosition.top,
          color: tag.color || "#f5faff",
          textColor: tag.textColor || "#000000",
          zIndex: ++this.zSeed,
        };
        const res = await DashboardEtiquetas.create(this.buildEtiquetaPayload(newNoteTag));
        newNoteTag.id = res?.data?.id;
        this.tags.push(newNoteTag);
        await this.refreshPlSummaryForTag(newNoteTag);
        return;
      }

      noteTag.title = noteTitle;
      noteTag.infoKey = "comportamiento_pl_nota";
      noteTag.presentationType = "nota";
      noteTag.fechaInicial = tag.fechaInicial || "";
      noteTag.fechaFinal = tag.fechaFinal || "";
      noteTag.arrendador = tag.arrendador || "";
      noteTag.tipoContrato = tag.tipoContrato || "";
      noteTag.nroContrato = tag.nroContrato || "";
      noteTag.tipoActivo = tag.tipoActivo || "";
      noteTag.detalle = String(tag.id);
      noteTag.width = noteWidth;
      noteTag.height = noteHeight;
      noteTag.color = tag.color || noteTag.color;
      noteTag.textColor = tag.textColor || noteTag.textColor;
      await this.persistTag(noteTag);
      await this.refreshPlSummaryForTag(noteTag);
    },
    resolveInfoKey(tag) {
      const rawInfoKey = String(tag?.infoKey || tag?.info_key || tag?.infoType || "");
      const allowed = this.infoOptions.map((opt) => opt.value);
      if (rawInfoKey === "comportamiento_pl_nota") return rawInfoKey;
      if (allowed.includes(rawInfoKey)) return rawInfoKey;
      return allowed[0] || rawInfoKey || "custom";
    },
    getRowsByInfoKey() {
      return [];
    },
    getDimensionKeyByInfoKey(infoKey) {
      return infoKey;
    },
    tableGroupTitle(tag) {
      return this.resolveInfoKey(tag);
    },
    dimensionValueFromRow(row, infoKey) {
      const key = this.getDimensionKeyByInfoKey(infoKey);
      let value = row?.[key];
      if (value === null || value === undefined || value === "") return this.labels.noValue;
      return String(value);
    },
    resolvePresentation(tag) {
      const infoKey = this.resolveInfoKey(tag || {});
      const allowed = this.presentationOptionsByInfoKey(infoKey).map((opt) => opt.value);
      if (allowed.includes(tag?.presentationType)) return tag.presentationType;
      const raw = String(tag?.presentationType || "").toLowerCase();
      if (infoKey === "comportamiento_pl") return "graficos";
      if (infoKey === "comportamiento_pl_nota") return "nota";
      const fallbackByRaw = [
        { check: () => raw.includes("horizontal"), value: "bars_horizontal" },
        { check: () => raw.includes("linea") || raw.includes("line"), value: "line" },
        { check: () => raw.includes("area"), value: "area" },
        { check: () => raw.includes("dona") || raw.includes("donut"), value: "donut" },
        { check: () => raw.includes("medidor") || raw.includes("gauge"), value: "gauge" },
        { check: () => raw.includes("progreso") || raw.includes("progress"), value: "progress" },
        { check: () => raw.includes("ranking") || raw.includes("rank"), value: "ranking" },
        { check: () => raw.includes("tabla") || raw.includes("table"), value: "table" },
      ];
      const rawMatch = fallbackByRaw.find((item) => item.check())?.value;
      if (rawMatch && allowed.includes(rawMatch)) return rawMatch;
      return allowed[0] || "table";
    },
    metricCount(tag) {
      const key = this.resolveInfoKey(tag);
      const rows = this.getRowsByInfoKey(key);
      return rows.reduce((acc, row) => acc + (Number(row.total_registros) || 0), 0);
    },
    metricLabel(tag) {
      const key = this.resolveInfoKey(tag);
      const match = this.infoOptions.find((opt) => opt.value === key);
      if (match) return match.label;
      return key;
    },
    presentationLabel(tag) {
      const value = this.resolvePresentation(tag);
      const infoKey = this.resolveInfoKey(tag);
      const scoped = this.presentationOptionsByInfoKey(infoKey);
      const match = scoped.find((opt) => opt.value === value) || this.presentationOptions.find((opt) => opt.value === value);
      return match ? match.label : value;
    },
    presentationThumbClass(value) {
      return `thumb-${String(value || "value").replace(/_/g, "-")}`;
    },
    metricSummary(tag) {
      const rows = this.getRowsByInfoKey(this.resolveInfoKey(tag));
      if (!rows.length) return this.labels.noValue;
      const fechas = this.comparativoFechas.join(" vs ");
      return this.isSpanish ? `Comparativo ${fechas}` : `Comparison ${fechas}`;
    },
    colorSeed(input) {
      const value = String(input || "seed");
      let hash = 0;
      for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
      }
      return Math.abs(hash);
    },
    randomColor(tag, key = "base", alpha = 0.95) {
      const presentation = this.resolvePresentation(tag || {});
      const base = `${tag?.id || tag?.title || "tag"}-${presentation}-${key}`;
      const seed = this.colorSeed(base);
      const corporatePalette = [
        "hsl(201, 73%, 42%)", // azul corporativo
        "hsl(196, 64%, 46%)", // teal
        "hsl(168, 46%, 43%)", // verde azulado
        "hsl(128, 42%, 49%)", // verde medio
        "hsl(98, 40%, 51%)",  // verde oliva suave
        "hsl(39, 78%, 48%)",  // ambar
        "hsl(30, 72%, 50%)",  // naranja corporativo
        "hsl(16, 64%, 52%)",  // naranja rojizo suave
        "hsl(351, 55%, 53%)", // rojo controlado
        "hsl(277, 36%, 51%)", // violeta sobrio
        "hsl(224, 48%, 52%)", // azul secundario
        "hsl(212, 57%, 47%)", // azul petróleo
        "hsl(186, 49%, 44%)", // cian sobrio
        "hsl(47, 70%, 46%)",  // mostaza
      ];
      const baseColor = corporatePalette[seed % corporatePalette.length];
      return baseColor.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
    },
    periodTotals(tag) {
      const rows = this.getRowsByInfoKey(this.resolveInfoKey(tag));
      const totals = {};
      rows.forEach((row) => {
        const period = String(row?.fecha_cierre || "");
        if (!period) return;
        totals[period] = (totals[period] || 0) + (Number(row?.total_registros) || 0);
      });
      return totals;
    },
    currentPeriod(tag) {
      const totals = this.periodTotals(tag);
      const fromFilters = this.comparativoFechas.find((period) => Object.prototype.hasOwnProperty.call(totals, period));
      if (fromFilters) return fromFilters;
      return Object.keys(totals).sort().reverse()[0] || "";
    },
    calculatePercent(value, total) {
      if (!total) return 0;
      return Math.max(Math.min(Math.round((value / total) * 100), 100), 0);
    },
    metricDetails(tag) {
      const totals = this.periodTotals(tag);
      const currentPeriod = this.currentPeriod(tag);
      const value = Number(totals[currentPeriod] || 0);
      const total = Object.values(totals).reduce((acc, item) => acc + (Number(item) || 0), 0);
      return {
        value,
        total,
        percentage: this.calculatePercent(value, total),
      };
    },
    pieComparisons(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const groups = this.buildComparativoGroups(infoKey, 5);
      if (!groups.length) return [];
      const periods = groups[0]?.periods || this.comparativoFechas;

      return periods.map((period) => {
        const slices = groups.map((group) => {
          const match = group.bars.find((bar) => bar.period === period);
          const value = Number(match?.value || 0);
          return {
            key: `${group.key}_${period}`,
            label: group.shortLabel,
            value,
            color: this.randomColor(tag, `pie_${group.key}`, 0.98),
          };
        });
        const total = slices.reduce((acc, slice) => acc + slice.value, 0);
        const normalized = total > 0
          ? slices.map((slice) => ({ ...slice, percent: this.calculatePercent(slice.value, total) }))
          : slices.map((slice) => ({ ...slice, percent: 0 }));
        return {
          period,
          total,
          slices: normalized,
          gradient: this.pieGradientFromSlices(normalized, total),
        };
      });
    },
    pieGradientFromSlices(slices, total) {
      if (!total) {
        return "conic-gradient(rgba(255,255,255,0.25) 0% 100%)";
      }
      let cursor = 0;
      const steps = slices.map((slice) => {
        const portion = (slice.value / total) * 100;
        const from = cursor;
        const to = cursor + portion;
        cursor = to;
        return `${slice.color} ${from}% ${to}%`;
      });
      return `conic-gradient(${steps.join(", ")})`;
    },
    pieLegend(tag) {
      const comparisons = this.pieComparisons(tag);
      if (!comparisons.length) return [];
      const current = comparisons[0];
      return current.slices
        .slice(0, 5)
        .map((slice) => ({
          key: slice.key,
          label: slice.label,
          color: slice.color,
          percent: slice.percent,
        }));
    },
    progressPercent(tag) {
      const details = this.metricDetails(tag);
      return details.percentage;
    },
    progressFillStyle(tag) {
      const start = this.randomColor(tag, "progress-start", 0.95);
      const end = this.randomColor(tag, "progress-end", 0.95);
      return {
        width: `${this.progressPercent(tag)}%`,
        background: `linear-gradient(90deg, ${start} 0%, ${end} 100%)`,
      };
    },
    lineStrokeStyle(tag) {
      return {
        stroke: this.randomColor(tag, "line", 0.95),
      };
    },
    areaFillStyle(tag) {
      return {
        fill: this.randomColor(tag, "area", 0.42),
      };
    },
    barItems(tag) {
      const series = this.seriesWithLabels(tag).map((item) => ({
        ...item,
        shortLabel: String(item.label || "")
          .split(" ")
          .slice(0, 2)
          .join(" ")
          .slice(0, 12),
      }));

      const maxValue = Math.max(...series.map((item) => item.value), 1);
      const total = series.reduce((acc, item) => acc + item.value, 0);
      return series.map((item, index) => ({
        key: item.key,
        label: item.label,
        shortLabel: item.shortLabel,
        value: item.value,
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        color: this.randomColor(tag, item.key, 0.95),
        height: Math.max(Math.round((item.value / maxValue) * 100), item.value > 0 ? 18 : 8),
      }));
    },
    groupedBarItems(tag) {
      return this.barItems(tag).map((item, index) => ({
        key: `${item.key}_${index}`,
        shortLabel: item.shortLabel,
        tooltip: `${item.label}: ${item.value} (${item.percentage}%)`,
        bars: [
          {
            period: "single",
            periodShort: "",
            value: item.value,
            avgRate: "",
            color: item.color,
            height: item.height,
          },
        ],
      }));
    },
    horizontalBarItems(tag) {
      const base = this.seriesWithLabels(tag).slice(0, 4);
      const maxValue = Math.max(...base.map((item) => item.value), 1);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base.map((item, index) => ({
        key: item.key,
        label: item.label.slice(0, 12),
        value: item.value,
        avgRate: item.avgRate || "",
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        color: this.randomColor(tag, item.key, 0.95),
        width: Math.max(Math.round((item.value / maxValue) * 100), 8),
      }));
    },
    seriesWithLabels() {
      return [];
    },
    pointTitle(point) {
      const ratePart = point?.avgRate ? ` | ${point.avgRate}` : "";
      return `${point.label}: ${point.value} (${point.percentage}%)${ratePart}`;
    },
    linePoints(tag) {
      const base = this.seriesWithLabels(tag);
      const maxValue = Math.max(...base.map((item) => item.value), 1);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base.slice(0, 6).map((item, index, all) => ({
        key: item.key,
        label: item.label,
        value: item.value,
        avgRate: item.avgRate || "",
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        x: all.length === 1 ? 50 : Math.round((index / (all.length - 1)) * 100),
        y: 100 - Math.round((item.value / maxValue) * 88),
      }));
    },
    linePolyline(tag) {
      return this.linePoints(tag).map((point) => `${point.x},${point.y}`).join(" ");
    },
    areaPolygon(tag) {
      const points = this.linePoints(tag);
      if (!points.length) return "0,100 100,100";
      const line = points.map((point) => `${point.x},${point.y}`).join(" ");
      return `${points[0].x},100 ${line} ${points[points.length - 1].x},100`;
    },
    gaugeStyle(tag) {
      const pct = this.progressPercent(tag);
      return {
        background: `conic-gradient(from 180deg, #7ec8e3 0% ${pct}%, rgba(255,255,255,0.22) ${pct}% 100%)`,
      };
    },
    rankingItems(tag) {
      const base = this.seriesWithLabels(tag);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base
        .sort((a, b) => b.value - a.value)
        .slice(0, 4)
        .map((item, index) => ({
          ...item,
          selected: index === 0,
          label: item.label.slice(0, 14),
          avgRate: item.avgRate || "",
          percentage: this.calculatePercent(item.value, total),
        }));
    },
    tableItems(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const rows = this.getRowsByInfoKey(infoKey);
      return rows.map((row, index) => ({
        key: `${this.dimensionValueFromRow(row, infoKey)}_${row.fecha_cierre}_${index}`,
        dimensionValue: this.dimensionValueFromRow(row, infoKey),
        fechaCierre: row.fecha_cierre,
        tasaPromedio: Number(row.tasa || 0).toFixed(4),
        totalRegistros: Number(row.total_registros || 0),
      }));
    },
    tagStyle(tag) {
      return {
        left: `${tag.left}px`,
        top: `${tag.top}px`,
        width: `${tag.width}px`,
        height: `${tag.height}px`,
        backgroundColor: tag.color,
        color: tag.textColor,
        zIndex: tag.zIndex,
      };
    },
    toRect(tag, override = {}) {
      return {
        left: Number(override.left ?? tag.left) || 0,
        top: Number(override.top ?? tag.top) || 0,
        width: Number(override.width ?? tag.width) || 0,
        height: Number(override.height ?? tag.height) || 0,
      };
    },
    rectsOverlap(a, b) {
      return (
        a.left < b.left + b.width &&
        a.left + a.width > b.left &&
        a.top < b.top + b.height &&
        a.top + a.height > b.top
      );
    },
    hasCollision(candidateRect, ignoreId = null) {
      return this.tags.some((tag) => {
        if (ignoreId !== null && tag.id === ignoreId) return false;
        return this.rectsOverlap(candidateRect, this.toRect(tag));
      });
    },
    getCanvasSize() {
      const fallbackWidth = 1200;
      const fallbackHeight = 780;
      if (!this.$refs.canvas) {
        return { width: fallbackWidth, height: fallbackHeight };
      }
      return {
        width: this.$refs.canvas.clientWidth || fallbackWidth,
        height: this.$refs.canvas.clientHeight || fallbackHeight,
      };
    },
    findAvailablePosition(width, height) {
      const { width: canvasWidth, height: canvasHeight } = this.getCanvasSize();
      const maxLeft = Math.max(canvasWidth - width, 0);
      const maxTop = Math.max(canvasHeight - height, 0);
      const step = 20;

      for (let top = 12; top <= maxTop; top += step) {
        for (let left = 12; left <= maxLeft; left += step) {
          const candidate = { left, top, width, height };
          if (!this.hasCollision(candidate)) {
            return { left, top };
          }
        }
      }

      return null;
    },
    notifyNoSpace() {
      if (this.$toast?.warning) {
        this.$toast.warning(this.labels.noSpace);
      }
    },
    async createTag() {
      if (!this.ensureCanManageTags()) return;
      if (this.isLimitReached) {
        this.notifyLimitReached();
        return;
      }

      const isBehaviorTag = this.draft.infoKey === "comportamiento_pl";
      let noteTag = null;
      const minSize = this.getTagMinSize(this.draft);
      const defaultWidth = Math.max(220, minSize.minWidth);
      const defaultHeight = Math.max(140, minSize.minHeight);
      const freePosition = this.findAvailablePosition(defaultWidth, defaultHeight);
      if (!freePosition) {
        this.notifyNoSpace();
        return;
      }

      const tag = {
        id: null,
        title: this.draft.title?.trim() || this.metricLabel(this.draft),
        infoKey: this.draft.infoKey,
        presentationType: this.draft.presentationType,
        fechaInicial: "",
        fechaFinal: "",
        arrendador: "",
        tipoContrato: "",
        nroContrato: "",
        tipoActivo: "",
        detalle: "",
        width: defaultWidth,
        height: defaultHeight,
        left: freePosition.left,
        top: freePosition.top,
        color: this.draft.color || "#f5faff",
        textColor: this.draft.textColor || "#000000",
        zIndex: ++this.zSeed,
      };

      try {
        const res = await DashboardEtiquetas.create(this.buildEtiquetaPayload(tag));
        tag.id = res?.data?.id;
        this.tags.push(tag);

        if (isBehaviorTag) {
          const noteSize = this.getTagMinSize({ infoKey: "comportamiento_pl_nota", presentationType: "nota" });
          const noteWidth = Math.max(260, noteSize.minWidth);
          const noteHeight = Math.max(180, noteSize.minHeight);
          const notePosition = this.findAvailablePosition(noteWidth, noteHeight);
          if (!notePosition) {
            throw new Error("NO_SPACE_NOTE");
          }

          noteTag = {
            id: null,
            title: this.labels.noteTitle || "NOTA A LOS ESTADOS FINANCIEROS",
            infoKey: "comportamiento_pl_nota",
            presentationType: "nota",
            fechaInicial: tag.fechaInicial,
            fechaFinal: tag.fechaFinal,
            arrendador: tag.arrendador,
            tipoContrato: tag.tipoContrato,
            nroContrato: tag.nroContrato,
            tipoActivo: tag.tipoActivo,
            detalle: String(tag.id),
            width: noteWidth,
            height: noteHeight,
            left: notePosition.left,
            top: notePosition.top,
            color: tag.color,
            textColor: tag.textColor,
            zIndex: ++this.zSeed,
          };

          const noteRes = await DashboardEtiquetas.create(this.buildEtiquetaPayload(noteTag));
          noteTag.id = noteRes?.data?.id;
          this.tags.push(noteTag);
          await this.refreshPlSummaryForTag(noteTag);
        }

        this.activeTagId = tag.id;
        await this.openTagEditor(tag.id);
        await this.refreshPlSummaryForTag(tag);
        this.resetDraft();
        this.showCreator = false;
      } catch (error) {
        const createdIds = [tag.id, noteTag?.id].filter(Boolean);
        await Promise.all(createdIds.map(async (createdId) => {
          try {
            await DashboardEtiquetas.delete(createdId);
          } catch (deleteError) {
            return null;
          }
          return null;
        }));
        this.tags = this.tags.filter((item) => item.id !== tag.id);
        if (noteTag?.id) {
          this.tags = this.tags.filter((item) => item.id !== noteTag.id);
        }
        this.$toast?.error?.("No se pudo crear la etiqueta en base de datos.");
      }
    },
    resetDraft() {
      this.draft = {
        title: "",
        infoKey: "comportamiento_pl",
        presentationType: "graficos",
        color: "#f5faff",
        textColor: "#000000",
      };
    },
    async deleteTag(tagId) {
      if (!this.ensureCanManageTags()) return;
      const confirmed = window.confirm(this.labels.confirmDeleteTag);
      if (!confirmed) return;
      try {
        const tag = this.tags.find((item) => item.id === tagId);
        const pairedNote = tag ? this.findComportamientoNotaForTag(tag) : null;
        await DashboardEtiquetas.delete(tagId);
        this.tags = this.tags.filter((tag) => tag.id !== tagId);
        if (tag && this.isComportamientoPl(tag) && pairedNote) {
          await DashboardEtiquetas.delete(pairedNote.id);
          this.tags = this.tags.filter((item) => item.id !== pairedNote.id);
        }
        if (this.activeTagId === tagId) this.activeTagId = null;
        if (this.searchTagId === tagId) this.searchTagId = null;
      } catch (error) {
        this.$toast?.error?.("No se pudo eliminar la etiqueta en base de datos.");
      }
    },
    startDrag(event, tagId) {
      if (!this.ensureCanManageTags(false)) return;
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "drag",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        left: tag.left,
        top: tag.top,
      };
    },
    startResize(event, tagId) {
      if (!this.ensureCanManageTags(false)) return;
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "resize",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        width: tag.width,
        height: tag.height,
      };
    },
    onPointerMove(event) {
      if (!this.interaction) return;
      const tag = this.tags.find((item) => item.id === this.interaction.tagId);
      if (!tag || !this.$refs.canvas) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const deltaX = event.clientX - this.interaction.startX;
      const deltaY = event.clientY - this.interaction.startY;

      if (this.interaction.type === "drag") {
        const maxLeft = Math.max(canvasRect.width - tag.width, 0);
        const maxTop = Math.max(canvasRect.height - tag.height, 0);
        const nextLeft = this.clamp(this.interaction.left + deltaX, 0, maxLeft);
        const nextTop = this.clamp(this.interaction.top + deltaY, 0, maxTop);
        const candidate = this.toRect(tag, { left: nextLeft, top: nextTop });

        if (!this.hasCollision(candidate, tag.id)) {
          tag.left = nextLeft;
          tag.top = nextTop;
        }
      } else if (this.interaction.type === "resize") {
        const minSize = this.getTagMinSize(tag);
        const maxWidth = Math.max(canvasRect.width - tag.left, minSize.minWidth);
        const maxHeight = Math.max(canvasRect.height - tag.top, minSize.minHeight);
        const nextWidth = this.clamp(this.interaction.width + deltaX, minSize.minWidth, maxWidth);
        const nextHeight = this.clamp(this.interaction.height + deltaY, minSize.minHeight, maxHeight);
        const candidate = this.toRect(tag, { width: nextWidth, height: nextHeight });

        if (!this.hasCollision(candidate, tag.id)) {
          tag.width = nextWidth;
          tag.height = nextHeight;
        }
      }
    },
    async onPointerUp() {
      if (!this.interaction) return;
      const tagId = this.interaction.tagId;
      this.interaction = null;
      const tag = this.tags.find((item) => item.id === tagId);
      if (tag) await this.persistTag(tag);
    },
    raiseTag(tag) {
      tag.zIndex = ++this.zSeed;
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    async loadTags() {
      try {
        const res = await DashboardEtiquetas.getAll();
        const parsed = Array.isArray(res?.data?.listDashboardEtiquetas) ? res.data.listDashboardEtiquetas : [];
        if (!Array.isArray(parsed)) return;
        this.tags = parsed
          .slice(0, this.maxTags)
          .map((rawTag, index) => {
            const normalizedTag = {
              id: rawTag.id || Date.now() + index,
              title: rawTag.titulo || this.labels.fallbackTagTitle,
              infoKey: rawTag.info_key || null,
              presentationType: rawTag.presentation_type || null,
              infoType: rawTag.infoType || null,
              fechaInicial: rawTag.pl_fecha_inicial || "",
              fechaFinal: rawTag.pl_fecha_final || "",
              arrendador: rawTag.pl_arrendador || "",
              tipoContrato: rawTag.pl_tipo_contrato || "",
              nroContrato: rawTag.pl_num_contrato || "",
              tipoActivo: rawTag.pl_tipo_activo || "",
              detalle: rawTag.pl_detalle || "",
              left: Math.max(Number(rawTag.pos_x) || 0, 0),
              top: Math.max(Number(rawTag.pos_y) || 0, 0),
              color: rawTag.color_fondo || "#f5faff",
              textColor: rawTag.color_texto || "#000000",
              zIndex: Number(rawTag.z_index) || index + 1,
            };
            normalizedTag.infoKey = this.resolveInfoKey(normalizedTag);
            normalizedTag.presentationType = this.resolvePresentation(normalizedTag);
            const minSize = this.getTagMinSize(normalizedTag);

            return {
              ...normalizedTag,
              width: this.clamp(Number(rawTag.width_px) || 220, minSize.minWidth, 480),
              height: this.clamp(Number(rawTag.height_px) || 140, minSize.minHeight, 320),
            };
          })
          .map((tag) => ({
            ...tag,
            infoKey: this.resolveInfoKey(tag),
            presentationType: this.resolvePresentation(tag),
          }));

        this.plSummaryCache = {};
        await this.refreshPlSummaries();

        this.tags.forEach((tag) => {
          const rect = this.toRect(tag);
          if (this.hasCollision(rect, tag.id)) {
            const freePosition = this.findAvailablePosition(tag.width, tag.height);
            if (freePosition) {
              tag.left = freePosition.left;
              tag.top = freePosition.top;
            }
          }
        });

        this.zSeed = this.tags.reduce((max, tag) => Math.max(max, tag.zIndex), 1);
      } catch (error) {
        this.tags = [];
      }
    },
    getPeriodosFallback() {
      const now = new Date();
      const lastClosed = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevYearSameMonth = new Date(lastClosed.getFullYear() - 1, lastClosed.getMonth(), 1);
      const toPeriod = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return [toPeriod(lastClosed), toPeriod(prevYearSameMonth)];
    },
    getDefaultComparedPeriod(periodoActual, periodos) {
      const base = String(periodoActual || "");
      if (!base || !Array.isArray(periodos) || !periodos.length) return "";
      const [year, month] = base.split("-");
      const periodoMismoMesAnioAnterior = `${Number(year) - 1}-${month}`;
      if (periodos.includes(periodoMismoMesAnioAnterior)) return periodoMismoMesAnioAnterior;
      const alterno = periodos.find((item) => item !== base);
      return alterno || base;
    },
    async loadPeriodosDisponibles() {
      return [];
    },
    async applyPeriodoFilter() {
      return [];
    },
    async loadMetricData() {
      this.comparativoFechas = [];
      this.comparativoTasasRows = [];
      this.comparativoTasasTipoContratoRows = [];
      this.comparativoTasasTerceroRows = [];
    },
    async loadContextData() {
      try {
        await Menu.getAllMenu();
      } catch (error) {
        // Sin acción: no bloquea métricas del dashboard.
      }
      await this.loadPlDependencies();
      this.canManageTags = String(this.$store.state.userRole) === "1";

      if (!this.canManageTags) {
        this.showCreator = false;
        this.cancelTagEdit();
      }
    },
  },
  mounted() {
    this.loadTags();
    this.canManageTags = String(this.$store.state.userRole) === "1";
    this.loadContextData();
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  },
  beforeUnmount() {
    if (this.terceroFilterDebounce) {
      clearTimeout(this.terceroFilterDebounce);
      this.terceroFilterDebounce = null;
    }
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  },
};
</script>

<style src="../main.css"></style>
